import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import ChartOfAccountsAccordion from "components/ChartOfAccountsAccordion/ChartOfAccountsAccordion";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BalanceMatchingBanner } from "components/DesignSystem/BalanceMatchingBanner/BalanceMatchingBanner";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { Search } from "components/DesignSystem/Search/Search";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import { LockDateToolTipContent } from "components/LockedDatePicker/LockedDatePicker";
import { LockedUi } from "components/LockedUi/LockedUi";
import { MergeCategories } from "components/MergeCategories/MergeCategories";
import { MoveCategories } from "components/MoveCategories/MoveCategories";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { CaretDown } from "components/icons/CaretDown";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { EditPencil } from "components/icons/EditPencil";
import { Info } from "components/icons/Info";
import { Padlock } from "components/icons/Padlock";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { parse, stringify } from "qs";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ThreeDots from "static/images/ThreeDots.svg";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  useGetCategoryActionsQuery,
  useGetCOAImportStatusQuery,
  useGetStartingDateQuery,
  useLazyGetCategoryUsageQuery,
  useLazyGetExportCOAQuery,
} from "store/apis/chartOfAccounts";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import {
  deleteCOAModalOpen,
  editCOAModalOpen,
  setBookingStartDateModal,
} from "store/slices/chartOfAccounts";
import { setOpenComment } from "store/slices/openComment";
import { TxnCategories } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { openLink } from "utils/openLink";
import { stopPropagation } from "utils/stopPropagation";
import { AddNewAccount } from "./AddNewAccount";
import { DeleteCategory } from "./DeleteCategory";
import { EditCategory } from "./EditCategory";
import { StartDateModal } from "./StartDateModal";

export const ExportCOA = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const [exportCOA, { isLoading: isExporting }] = useLazyGetExportCOAQuery();

  const onExport = async (exportType: "PDF" | "EXCEL") => {
    try {
      const { download_url } = await exportCOA({
        groupId,
        entityId,
        exportType,
        hiddenCategory: ["BANK_TRANSFER", "PAY_DOWN_CREDIT"],
      }).unwrap();
      openLink(download_url);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group" disabled={isExporting}>
        <div>
          <Button
            size="xsmall"
            onClick={(e) => e.stopPropagation()}
            isLoading={isExporting}
            disabled={isExporting}
          >
            <div className="t-flex t-items-center t-gap-1.5">
              Export
              <div className="group-data-state-open:t-rotate-180">
                <CaretDown />
              </div>
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} align="end" side="bottom">
          <Dropdown.Item onSelect={() => onExport("PDF")}>PDF</Dropdown.Item>
          <Dropdown.Item onSelect={() => onExport("EXCEL")}>
            Excel
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const CategoryAction = ({
  children,
}: {
  children: ({ close }: { close: () => void }) => ReactNode;
}) => {
  const dropdown = useModal();

  return (
    <Dropdown.Root open={dropdown.isOpen} onOpenChange={dropdown.toggle}>
      {children({ close: dropdown.close })}
    </Dropdown.Root>
  );
};

const getParentTree = (
  categories: TxnCategories[],
  selectedCategoryId: string,
  currentParents: string[] = []
): string[] | undefined => {
  for (const category of categories) {
    if (category.uuid === selectedCategoryId) {
      return currentParents;
    }

    if (category.types) {
      const parent = getParentTree(category.types, selectedCategoryId, [
        category.uuid,
        ...currentParents,
      ]);
      if (parent?.length) {
        return parent;
      }
    }
  }
};

export const COATable = ({
  commentPane,
  withoutTopPadding = false,
}: {
  commentPane?: ReturnType<typeof useModal>;
  withoutTopPadding?: boolean;
}) => {
  usePageTitle("Chart of Accounts");
  const dispatch = useDispatch();
  const { update } = useUpdateQuery();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const query = useQuery();
  const searchTerm = query.get("search_term") || "";
  const [checkedCategories, setCheckedCategories] = useState<TxnCategories[]>(
    []
  );
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const mergeCategories = useModal();
  const moveCategories = useModal();
  const addSubAccount = useModal();
  const [subAccountParentId, setSubAccountParentId] = useState<string | null>(
    null
  );
  const openComment = useAppSelector((state) => state.openComment.openComment);

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.category?.uuid) {
      update({
        query: "selected_category_id",
        value: comment.category?.uuid,
      });
    }
    if (comment.category) {
      dispatch(setOpenComment(comment.category.uuid));
    }
  };

  const selectedCategory = query.get("selected_category_id");
  const openCommentParam = query.get("open_comment");

  const {
    chartOfAccounts: COAData,
    opening_balance_summary,
    isLoading,
    isSuccess,
  } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_TRANSFER", "PAY_DOWN_CREDIT"],
    search: searchTerm,
  });

  const selectedParentTree = selectedCategory
    ? getParentTree(COAData, selectedCategory)
    : [];

  const {
    data: COAImportStatus,
    isLoading: loading,
    isSuccess: success,
  } = useGetCOAImportStatusQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const { data: categoryActions } = useGetCategoryActionsQuery(
    {
      entityId,
      category_ids: checkedCategories.map((c) => c.uuid),
    },
    {
      skip: checkedCategories.length === 0 || !entityId,
    }
  );

  const [
    getCategoryUsage,
    {
      currentData,
      isLoading: isLoadingCategoryUsage,
      isFetching: isFetchingCategoryUsage,
      originalArgs,
    },
  ] = useLazyGetCategoryUsageQuery();

  const gettingCategoryUsage =
    isLoadingCategoryUsage || isFetchingCategoryUsage;

  const { data: startDateData } = useGetStartingDateQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const { start_date } = startDateData || {};

  const editHandler = ({ uuid }: TxnCategories) => {
    dispatch(editCOAModalOpen(uuid));
  };

  const { alertToast } = useToast();

  const deleteHandler = async ({ uuid }: { uuid: string }) => {
    try {
      const usage = await getCategoryUsage({
        entityId: entityId,
        categoryId: uuid,
        groupId: groupId,
      }).unwrap();

      if (usage.products_and_services > 0) {
        return alertToast({
          message:
            "The account you are trying to delete is being used in 'My Products', please update and try again.",
        });
      }

      dispatch(deleteCOAModalOpen(uuid));
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search_term", value: value || null });
  });

  const openBookkeepingStartDateModal = () => {
    dispatch(setBookingStartDateModal(true));
  };

  const isEmpty = COAData.length === 0;

  const onCheck = (category: TxnCategories, isChecked: boolean) => {
    const traverseAndAddCategories = (category: TxnCategories) => {
      if (category.types && category.types?.length > 0) {
        category.types.forEach((c) => {
          traverseAndAddCategories(c);
        });
      }

      return setCheckedCategories((categories) => {
        if (categories.find((c) => c.uuid === category.uuid)) {
          return categories;
        }

        return [...categories, category];
      });
    };

    const traverseAndRemoveCategories = (category: TxnCategories) => {
      if (category.types && category.types?.length > 0) {
        setCheckedCategories((categories) =>
          categories.filter((c) => c.uuid !== category.uuid)
        );
        category.types.forEach((c) => {
          traverseAndRemoveCategories(c);
        });
      }

      setCheckedCategories((categories) =>
        categories.filter((c) => c.uuid !== category.uuid)
      );
    };

    const removeCategoryAndUnCheckParentChain = (
      category: TxnCategories,
      currentCategory: TxnCategories
    ): boolean => {
      if (currentCategory.uuid === category.uuid) {
        traverseAndRemoveCategories(currentCategory);
        return true;
      }

      if (category.types && category.types?.length > 0) {
        for (const ca of category.types) {
          const hasCategory = removeCategoryAndUnCheckParentChain(
            ca,
            currentCategory
          );

          if (hasCategory) {
            setCheckedCategories((categories) =>
              categories.filter((ca) => ca.uuid !== category.uuid)
            );
            return hasCategory;
          }
        }

        return false;
      }

      return false;
    };

    if (isChecked) {
      return traverseAndAddCategories(category);
    }

    for (const COA of COAData) {
      new Promise<void>((res) => {
        const done = removeCategoryAndUnCheckParentChain(COA, category);
        if (done) {
          res();
        }
      });
    }
  };

  const onMergeSuccess = () => {
    setCheckedCategories([]);
    mergeCategories.close();
  };

  const onMoveSuccess = () => {
    setCheckedCategories([]);
    moveCategories.close();
  };

  const hasDifferentParent =
    !categoryActions?.is_categories_mergable.error.has_same_parent ||
    !categoryActions?.is_categories_movable.error.has_same_parent;

  const selectedHasParentCategories =
    categoryActions?.is_categories_mergable.error.is_leaf_category === false;

  const moveError = hasDifferentParent
    ? "Cannot move between different category types"
    : "Cannot move selected categories";
  let mergeError = hasDifferentParent
    ? "Cannot merge categories from different types"
    : "Cannot merge selected categories";

  if (selectedHasParentCategories) {
    mergeError = "Cannot merge categories which has children";
  }

  if (checkedCategories.length < 2) {
    mergeError = "Select minimum 2 categories to start merging";
  }

  const importIsInProgress = COAImportStatus?.status === "IN_PROGRESS";
  const location = useLocation();
  const history = useHistory();

  const checkActiveCategory = (
    ref: HTMLElement | null,
    category: TxnCategories
  ) => {
    if (category.uuid === selectedCategory && ref) {
      ref.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  useEffect(() => {
    let currentSearch = parse(location.search, { ignoreQueryPrefix: true });
    let timeout: NodeJS.Timeout;
    if (selectedCategory) {
      if (currentSearch) {
        delete currentSearch.selected_category_id;
      }

      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }

    return () => clearTimeout(timeout);
  }, [history, location.pathname, location.search, selectedCategory]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  const {
    difference,
    assets,
    equity,
    expense,
    liability,
    revenue,
    total_equity,
  } = opening_balance_summary || {};

  return (
    <Async.Root
      isLoading={loading}
      isSuccess={success}
      isEmpty={importIsInProgress}
    >
      <Async.Empty>
        <div className="t-flex t-flex-col t-items-center t-justify-center t-h-[30rem] t-text-text-60 t-gap-3">
          <EmptyInvoiceList />
          <p className="t-m-0 t-text-subtitle-sm">
            Importing chart of accounts
          </p>
          <p className="t-m-0 t-text-body-sm">
            This will take some time, we will notify you once done
          </p>
        </div>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-h-full t-pl-5 sm:t-pl-4">
          <div
            className={classNames(
              "t-relative t-gap-5 t-flex t-flex-col t-flex-1 t-min-w-0 t-overflow-x-auto sm:t-overflow-x-hidden sm:t-pr-4",
              {
                "t-pr-3": commentPane?.isOpen,
                "t-pr-10": !commentPane?.isOpen,
              }
            )}
          >
            <div className="t-flex t-justify-between t-gap-5 t-bg-white t-z-filter-head t-sticky t-top-0 t-flex-col sm:t-gap-3 sm:t-static">
              <div className="t-flex t-justify-between sm:t-flex-col sm:t-gap-2">
                <div className="t-w-1/2 sm:t-w-full">
                  <Search
                    block
                    onChange={handleChange}
                    placeholder="Search"
                    defaultValue={searchTerm || ""}
                  />
                </div>
                <div className="t-flex t-items-center t-gap-1">
                  <div className="t-flex t-text-body-sm t-gap-1 t-ml-auto sm:t-ml-0 sm:t-whitespace-nowrap">
                    <div className="t-text-text-30">Starting date: </div>
                    <div>{dayjs(start_date).format(DISPLAY_DATE)}</div>
                  </div>
                  <LockedUi
                    feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                    blockedUi={
                      <LockedButton customType="ghost_icon" size="xsmall" />
                    }
                  >
                    <ConditionalToolTip
                      /* @tw */
                      propClass="t-w-64 !t-text-left react-datepicker-ignore-onclickoutside"
                      alignOffset={-10}
                      align="end"
                      keepOpenOnActivation
                      condition={
                        bookkeepingSettings?.accounting_lock_date && (
                          <LockDateToolTipContent text="The starting date cannot be edited since the financials are locked." />
                        )
                      }
                    >
                      <span>
                        <Button
                          disabled={Boolean(
                            bookkeepingSettings?.accounting_lock_date
                          )}
                          customType="ghost_icon"
                          size="small"
                          onClick={openBookkeepingStartDateModal}
                        >
                          <span className="">
                            <EditPencil className="t-size-4" />
                          </span>
                        </Button>
                      </span>
                    </ConditionalToolTip>
                  </LockedUi>
                </div>
              </div>
              {opening_balance_summary && (
                <BalanceMatchingBanner
                  state={difference === 0 ? "success" : "error"}
                  statusText={
                    difference === 0
                      ? "Opening balances matching"
                      : "Opening balances not matching"
                  }
                  formulaText={
                    difference === 0
                      ? "Assets = Liabilities + Equity"
                      : "Assets ≠ Liabilities + Equity"
                  }
                  dateRange={
                    start_date
                      ? `as of ${dayjs(start_date).format(DISPLAY_DATE)}`
                      : undefined
                  }
                >
                  <div className="t-contents sm:t-flex sm:t-w-full sm:t-flex-col sm:t-gap-3">
                    <div className="t-flex t-flex-col t-gap-1 sm:t-flex-row sm:t-justify-between">
                      <span className="t-text-text-30 t-text-body-sm">
                        Assets
                      </span>
                      <AmountSuperScript amount={assets || 0} />
                    </div>
                    <span
                      className={classNames(
                        "t-self-center t-text-body-lg sm:t-hidden",
                        {
                          "t-text-text-30": difference === 0,
                          "t-text-red-70": difference !== 0,
                        }
                      )}
                    >
                      {difference === 0 ? "=" : "≠"}
                    </span>
                    <div className="t-flex t-flex-col t-gap-1 sm:t-flex-row sm:t-justify-between">
                      <span className="t-text-text-30 t-text-body-sm">
                        Liability
                      </span>
                      <AmountSuperScript amount={liability || 0} />
                    </div>
                    <span className="t-text-text-30 t-self-center t-text-body-lg sm:t-hidden">
                      +
                    </span>
                    <div className="t-flex t-flex-col t-gap-1 t-self-end sm:t-self-stretch sm:t-flex-row sm:t-justify-between">
                      <span className="t-text-text-30 t-text-body-sm">
                        Equity
                      </span>
                      <div className="t-flex t-items-center t-gap-1.5 sm:t-flex-col sm:t-items-end sm:t-gap-0.5">
                        <AmountSuperScript amount={total_equity || 0} />
                        <div className="t-flex t-items-center t-gap-0.5 t-text-text-30 t-text-body-xs">
                          <ToolTip
                            collisionPadding={16}
                            text={
                              <i>
                                Total:{" "}
                                <AmountSuperScript amount={total_equity || 0} />{" "}
                                =
                                <AmountSuperScript amount={equity || 0} />{" "}
                                (Equity) +{" "}
                                <AmountSuperScript amount={revenue || 0} />{" "}
                                (Total Revenue) -{" "}
                                <AmountSuperScript amount={expense || 0} />{" "}
                                (Total Expense)
                              </i>
                            }
                          >
                            <div className="t-flex t-items-center t-gap-0.5 t-text-caption">
                              (<span>See how it's calculated</span>
                              <Info size="12" />)
                            </div>
                          </ToolTip>
                        </div>
                      </div>
                    </div>
                  </div>
                </BalanceMatchingBanner>
              )}
            </div>
            <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
              <Async.Empty>
                <EmptyScreen text="No Chart of Accounts found"></EmptyScreen>
              </Async.Empty>
              <Async.Success>
                <div className="t-relative t-max-w-full t-overflow-auto t-pb-28 sm:t-shrink-0">
                  <ChartOfAccountsAccordion.Root className="t-min-w-full sm:t-w-max">
                    <ChartOfAccountsAccordion.Header
                      widthType={commentPane?.isOpen ? "max" : "full"}
                    >
                      <div className="t-flex t-items-center t-justify-between t-w-full t-gap-2">
                        <div className="t-w-80 t-pl-3 t-uppercase sm:t-w-48">
                          Account
                        </div>
                        <div className="t-w-28 t-uppercase">Account ID</div>
                        <div className="t-w-32 t-uppercase t-flex t-justify-end">
                          OPENING BALANCE
                        </div>
                        <div className="t-w-36 t-uppercase t-flex t-justify-center">
                          AS ON DATE
                        </div>
                      </div>
                      <div className="t-flex t-w-44 t-justify-between t-gap-1">
                        <div className="t-uppercase">Description</div>
                      </div>
                      <div className="t-w-10" />
                    </ChartOfAccountsAccordion.Header>
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <ChartOfAccountsAccordion.Trigger
                        selected={selectedCategory}
                        refFun={checkActiveCategory}
                        openCategories={selectedParentTree}
                        widthType={commentPane?.isOpen ? "max" : "full"}
                        checkedCategories={checkedCategories}
                        onCheck={onCheck}
                        data={COAData}
                        action={(category, level) => (
                          <LockedUi
                            feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                            blockedUi={
                              <Button
                                size="small"
                                customType="ghost_icon"
                                disabled
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Padlock />
                              </Button>
                            }
                          >
                            <CategoryAction>
                              {({ close }) => (
                                <AddCommentPopoverRoot
                                  contentId={category.uuid}
                                >
                                  <Dropdown.Trigger
                                    asChild
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div>
                                      <Popover.Anchor asChild>
                                        <Button
                                          size="small"
                                          customType="ghost_icon"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <img
                                            src={ThreeDots}
                                            alt="Action"
                                            className="t-select-none"
                                          />
                                        </Button>
                                      </Popover.Anchor>
                                    </div>
                                  </Dropdown.Trigger>
                                  <Popover.Portal>
                                    <AddCommentPop
                                      commentType={
                                        COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT
                                      }
                                      contentId={category.uuid}
                                    />
                                  </Popover.Portal>
                                  <Dropdown.Portal>
                                    <Dropdown.Content
                                      sideOffset={4}
                                      align="end"
                                    >
                                      <Popover.Trigger
                                        asChild
                                        onClick={stopPropagation}
                                      >
                                        <Dropdown.Item>
                                          Add comment
                                        </Dropdown.Item>
                                      </Popover.Trigger>
                                      <LockedUi
                                        feature={
                                          WRITE_PERMISSION_KEYS.ACCOUNTING
                                        }
                                      >
                                        <>
                                          <Dropdown.Item
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={() => {
                                              setSubAccountParentId(
                                                category.uuid
                                              );
                                              addSubAccount.open();
                                              close();
                                            }}
                                          >
                                            Add sub account
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={() =>
                                              editHandler(category)
                                            }
                                            disabled={level === 0}
                                          >
                                            Edit account
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            type="danger"
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={async (e) => {
                                              e.preventDefault();
                                              await deleteHandler({
                                                uuid: category.uuid,
                                              });
                                              close();
                                            }}
                                            disabled={
                                              Boolean(category?.types) ||
                                              category.is_system_category ||
                                              (gettingCategoryUsage &&
                                                originalArgs?.categoryId ===
                                                  category.uuid) ||
                                              level === 0
                                            }
                                            className="t-text-red"
                                          >
                                            <div className="t-flex t-gap-1 t-items-center">
                                              {gettingCategoryUsage &&
                                                originalArgs?.categoryId ===
                                                  category.uuid && (
                                                  <span className="t-flex t-origin-center t-animate-spin t-mr-1.5">
                                                    <LoadingIcon />
                                                  </span>
                                                )}
                                              <ConditionalToolTip
                                                condition={
                                                  Boolean(category?.types) &&
                                                  "Can't delete a parent with multiple accounts "
                                                }
                                              >
                                                <span>Delete account</span>
                                              </ConditionalToolTip>
                                            </div>
                                          </Dropdown.Item>
                                        </>
                                      </LockedUi>
                                    </Dropdown.Content>
                                  </Dropdown.Portal>
                                </AddCommentPopoverRoot>
                              )}
                            </CategoryAction>
                          </LockedUi>
                        )}
                      />
                    )}
                  </ChartOfAccountsAccordion.Root>
                </div>
              </Async.Success>
              <DeleteCategory />
              <EditCategory />
            </Async.Root>
            <StartDateModal />

            {addSubAccount.isOpen && (
              <AddNewAccount
                isOpen={addSubAccount.isOpen}
                close={() => {
                  addSubAccount.close();
                  setSubAccountParentId(null);
                }}
                open={addSubAccount.open}
                initialAccountType={subAccountParentId ?? undefined}
              />
            )}

            <Modal.Root
              open={mergeCategories.isOpen}
              onOpenChange={mergeCategories.close}
            >
              <Modal.Content>
                <MergeCategories
                  categories={checkedCategories}
                  onSuccess={onMergeSuccess}
                />
              </Modal.Content>
            </Modal.Root>

            <Modal.Root
              open={moveCategories.isOpen}
              onOpenChange={moveCategories.close}
            >
              <Modal.Content useCustomOverlay>
                <MoveCategories
                  categories={checkedCategories}
                  onSuccess={onMoveSuccess}
                />
              </Modal.Content>
            </Modal.Root>
          </div>

          <SelectedFloatingControl>
            {checkedCategories.length > 0 && (
              <>
                <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
                  {checkedCategories.length} selected
                </span>
                <span className="t-text-neutral-10 t-h-full">
                  <DividerLine />
                </span>
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                  blockedUi={
                    <>
                      <LockedButton text="Merge" />
                      <LockedButton text="Move" />
                    </>
                  }
                >
                  <ConditionalToolTip
                    condition={
                      !categoryActions?.is_categories_mergable.value
                        ? mergeError
                        : checkedCategories.length < 2
                        ? "Select at least two account to merge"
                        : null
                    }
                  >
                    <span>
                      <Button
                        disabled={
                          checkedCategories.length < 2 ||
                          !categoryActions?.is_categories_mergable.value
                        }
                        onClick={mergeCategories.open}
                        size="small"
                        customType="primary_outlined"
                      >
                        Merge
                      </Button>
                    </span>
                  </ConditionalToolTip>
                  <ConditionalToolTip
                    condition={
                      !categoryActions?.is_categories_movable.value && moveError
                    }
                  >
                    <span>
                      <Button
                        disabled={!categoryActions?.is_categories_movable.value}
                        onClick={moveCategories.open}
                        size="small"
                        customType="primary_outlined"
                      >
                        Move
                      </Button>
                    </span>
                  </ConditionalToolTip>
                </LockedUi>
                <Button
                  customType="ghost_icon"
                  size="xsmall"
                  onClick={() => setCheckedCategories([])}
                >
                  <Cross />
                </Button>
              </>
            )}
          </SelectedFloatingControl>

          <AnimatePresence>
            {commentPane?.isOpen && (
              <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
                <CommentsSlider
                  activeComment={openComment}
                  setActiveComment={setActiveComment}
                  onClose={commentPane?.toggle}
                  commentTypes={[COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT]}
                />
              </SliderAnimation>
            )}
          </AnimatePresence>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
